// Inline drawing overlay for Apple Pencil draw mode.
// When activated, covers the note body with a transparent SVG canvas.
// Pen strokes are pressure-sensitive; on save the drawing is embedded as an
// ink-drawing blot at the current cursor position.

import Quill from 'quill';
import { getStroke } from 'perfect-freehand';
import { acceptsInk, inkPoint, inkSamples, inkRenderer, finishInkStroke, inkStrokeHits } from './ink-input';

type Pt = [number, number, number]; // x, y, pressure

interface Stroke {
  points: Pt[];
  color: string;
  size: number;
}

interface AttachmentRef {
  id: string;
  blob_hash: string;
  filename: string;
}

export type OverlayUploadFn = (svgText: string, filename: string) => Promise<AttachmentRef>;

interface OverlayState {
  active: boolean;
  strokes: Stroke[];
  redoStack: Stroke[];
  current: Stroke | null;
  tool: 'pen' | 'eraser';
  color: string;
  size: number;
  width: number;
  height: number;
  activePointerId: number | null;
  quill: Quill | null;
  uploadFn: OverlayUploadFn | null;
  onDeactivate: (() => void) | null;
}

const ov: OverlayState = {
  active: false,
  strokes: [],
  redoStack: [],
  current: null,
  tool: 'pen',
  color: '#7aa2f7',
  size: 4,
  width: 0,
  height: 0,
  activePointerId: null,
  quill: null,
  uploadFn: null,
  onDeactivate: null,
};
let saving: Promise<boolean> | null = null;
let insertionIndex = 0;
let confirmDiscard: (() => Promise<boolean>) | null = null;

function $<T extends HTMLElement>(id: string): T {
  return document.getElementById(id) as T;
}

function svgPathFromOutline(pts: number[][]): string {
  if (!pts.length) return '';
  const d: (string | number)[] = ['M', pts[0]![0]!, pts[0]![1]!, 'Q'];
  for (let i = 0; i < pts.length; i++) {
    const [x0, y0] = pts[i]!;
    const [x1, y1] = pts[(i + 1) % pts.length]!;
    d.push(x0!, y0!, (x0! + x1!) / 2, (y0! + y1!) / 2);
  }
  d.push('Z');
  return d.join(' ');
}

function strokePathD(s: Stroke): string {
  const outline = getStroke(s.points, {
    size: s.size,
    thinning: 0.55,
    smoothing: 0.5,
    streamline: 0.5,
    simulatePressure: false,
  });
  return svgPathFromOutline(outline as number[][]);
}

const rerenderSvg = inkRenderer('ink-overlay-svg', strokePathD, () => ov);

function ptFromEvent(e: PointerEvent, svg: SVGSVGElement): Pt {
  return inkPoint(e, svg);
}

function eraseAt(x: number, y: number) {
  const r = Math.max(ov.size * 1.5, 12);
  const before = ov.strokes.length;
  ov.strokes = ov.strokes.filter((s) => !inkStrokeHits(s, x, y, r));
  if (ov.strokes.length !== before) {
    ov.redoStack = [];
    rerenderSvg();
  }
}

function onSvgDown(e: PointerEvent) {
  if (saving || !acceptsInk(e)) return;
  if (ov.activePointerId !== null && ov.activePointerId !== e.pointerId) return;
  e.preventDefault();
  const svg = $('ink-overlay-svg') as unknown as SVGSVGElement;
  svg.setPointerCapture(e.pointerId);
  ov.activePointerId = e.pointerId;
  const [x, y, p] = ptFromEvent(e, svg);
  if (ov.tool === 'eraser') { eraseAt(x, y); return; }
  ov.current = { points: [[x, y, p]], color: ov.color, size: ov.size };
  ov.redoStack = [];
  rerenderSvg();
}

function onSvgMove(e: PointerEvent) {
  if (ov.activePointerId !== e.pointerId) return;
  const svg = $('ink-overlay-svg') as unknown as SVGSVGElement;
  for (const sample of inkSamples(e)) {
    const point = ptFromEvent(sample, svg);
    if (ov.tool === 'eraser') eraseAt(point[0], point[1]);
    else ov.current?.points.push(point);
  }
  rerenderSvg();
}

function onSvgUp(e: PointerEvent) {
  if (ov.activePointerId !== e.pointerId) return;
  ov.activePointerId = null;
  if (ov.current?.points.length) {
    finishInkStroke(ov.current, e, $('ink-overlay-svg') as unknown as SVGSVGElement);
    ov.strokes.push(ov.current);
  }
  ov.current = null;
  rerenderSvg();
}

function setTool(tool: 'pen' | 'eraser') {
  ov.tool = tool;
  $('iov-pen-btn').classList.toggle('active', tool === 'pen');
  $('iov-eraser-btn').classList.toggle('active', tool === 'eraser');
}

function setColor(color: string) {
  ov.color = color;
  document.querySelectorAll('[data-ov-color]').forEach((el) =>
    el.classList.toggle('active', (el as HTMLElement).dataset.ovColor === color),
  );
  setTool('pen');
}

function undoStroke() {
  if (saving) return;
  const last = ov.strokes.pop();
  if (last) { ov.redoStack.push(last); rerenderSvg(); }
}

function buildSavedSvg(): string {
  if (!ov.strokes.length) {
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${ov.width} ${ov.height}"></svg>`;
  }
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const s of ov.strokes) {
    const r = Math.max(s.size, 1);
    for (const [x, y] of s.points) {
      if (x - r < minX) minX = x - r;
      if (y - r < minY) minY = y - r;
      if (x + r > maxX) maxX = x + r;
      if (y + r > maxY) maxY = y + r;
    }
  }
  const pad = 16;
  minX -= pad; minY -= pad;
  maxX += pad; maxY += pad;
  const w = Math.max(1, maxX - minX), h = Math.max(1, maxY - minY);
  const paths = ov.strokes.map((s) => `<path d="${strokePathD(s)}" fill="${s.color}"/>`).join('');
  const meta = JSON.stringify({ v: 1, w: ov.width, h: ov.height, strokes: ov.strokes });
  const safeMeta = meta.replace(/]]>/g, ']]]]><![CDATA[>');
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${minX} ${minY} ${w} ${h}" width="${w}" height="${h}">`
    + `<metadata id="swatnotes-ink"><![CDATA[${safeMeta}]]></metadata>`
    + paths + `</svg>`
  );
}

function positionOverlay() {
  const noteBody = document.getElementById('note-body')!;
  const rect = noteBody.getBoundingClientRect();
  const el = $('ink-overlay');
  el.style.left = rect.left + 'px';
  el.style.top = rect.top + 'px';
  el.style.width = rect.width + 'px';
  el.style.height = rect.height + 'px';
  ov.width = Math.max(ov.width, 320, Math.round(rect.width));
  ov.height = Math.max(ov.height, 240, Math.round(rect.height));
  const svg = $('ink-overlay-svg') as unknown as SVGSVGElement;
  svg.setAttribute('viewBox', `0 0 ${ov.width} ${ov.height}`);
  svg.setAttribute('width', String(ov.width));
  svg.setAttribute('height', String(ov.height));
}

function clearOverlayState() {
  ov.strokes = []; ov.redoStack = []; ov.current = null; ov.activePointerId = null;
  rerenderSvg();
}

function hideOverlay() {
  ov.active = false;
  document.body.classList.remove('ink-drawing');
  clearOverlayState();
  $('ink-overlay').classList.add('hidden');
  $('ink-overlay').classList.remove('iov-active');
  if (ov.quill) {
    ov.quill.root.removeAttribute('inputmode');
    ov.quill.enable(true);
  }
  ov.onDeactivate?.();
}

export function activateDrawMode() {
  if (ov.active) return;
  ov.active = true;
  ov.width = 0; ov.height = 0;
  insertionIndex = ov.quill?.getSelection()?.index ?? Math.max(0, (ov.quill?.getLength() ?? 1) - 1);
  ov.quill?.blur();
  ov.quill?.enable(false);
  clearOverlayState();
  setTool('pen');
  // Use blue as default — visible on both light and dark note backgrounds.
  setColor(ov.color);
  const saveBtn = $<HTMLButtonElement>('iov-save-btn');
  const cancelBtn = $<HTMLButtonElement>('iov-cancel-btn');
  saveBtn.disabled = false; cancelBtn.disabled = false;
  saveBtn.textContent = 'Done';
  $('iov-error').textContent = '';
  // Touch layouts hide the editor chrome so the canvas gets the full height;
  // measure the note body only after that layout change.
  document.body.classList.add('ink-drawing');
  positionOverlay();
  $('ink-overlay').classList.remove('hidden');
  $('ink-overlay').classList.add('iov-active');
  rerenderSvg();
  // Suppress iOS Scribble while the drawing canvas is active.
  if (ov.quill) (ov.quill.root as HTMLElement).setAttribute('inputmode', 'none');
}

export function deactivateDrawMode() {
  hideOverlay();
}

export function isDrawModeActive(): boolean {
  return ov.active;
}

export function hasUnsavedInk(): boolean {
  return ov.active && (ov.strokes.length > 0 || ov.current !== null || saving !== null);
}

export async function finishDrawMode(): Promise<boolean> {
  if (saving) return saving;
  if (!ov.active) return true;
  if (ov.current) { ov.strokes.push(ov.current); ov.current = null; }
  ov.activePointerId = null;
  if (!ov.strokes.length) { hideOverlay(); return true; }
  const saveBtn = $<HTMLButtonElement>('iov-save-btn');
  const cancelBtn = $<HTMLButtonElement>('iov-cancel-btn');
  saveBtn.disabled = cancelBtn.disabled = true;
  saveBtn.textContent = 'Saving…';
  saving = (async () => {
    try {
      const att = await ov.uploadFn!(buildSavedSvg(), `drawing-${Date.now()}.svg`);
      const q = ov.quill!;
      q.enable(true);
      q.insertEmbed(Math.min(insertionIndex, q.getLength() - 1), 'ink-drawing', {
        attachmentId: att.id, blobHash: att.blob_hash, filename: att.filename,
      }, 'user');
      hideOverlay();
      return true;
    } catch (error) {
      $('iov-error').textContent = `Drawing not saved: ${(error as Error).message}. Your ink is still here; retry Done.`;
      return false;
    } finally {
      saveBtn.disabled = cancelBtn.disabled = false;
      saveBtn.textContent = 'Done';
    }
  })();
  try { return await saving; } finally { saving = null; }
}

export function installDrawOverlay(
  quill: Quill,
  uploadFn: OverlayUploadFn,
  onDeactivate: () => void,
  onConfirmDiscard: () => Promise<boolean>,
) {
  ov.quill = quill;
  ov.uploadFn = uploadFn;
  ov.onDeactivate = onDeactivate;
  confirmDiscard = onConfirmDiscard;

  const svg = $('ink-overlay-svg') as unknown as SVGSVGElement;
  svg.addEventListener('pointerdown', onSvgDown);
  svg.addEventListener('pointermove', onSvgMove);
  svg.addEventListener('pointerup', onSvgUp);
  svg.addEventListener('pointercancel', onSvgUp);
  svg.addEventListener('lostpointercapture', onSvgUp);
  svg.addEventListener('touchstart', (e) => e.preventDefault(), { passive: false });
  svg.addEventListener('touchmove', (e) => e.preventDefault(), { passive: false });

  document.querySelectorAll('[data-ov-color]').forEach((el) =>
    el.addEventListener('click', () => setColor((el as HTMLElement).dataset.ovColor!)),
  );

  $('iov-pen-btn').addEventListener('click', () => setTool('pen'));
  $('iov-eraser-btn').addEventListener('click', () => setTool('eraser'));
  $('iov-undo-btn').addEventListener('click', undoStroke);
  $('iov-cancel-btn').addEventListener('click', async () => {
    if (saving) return;
    if (!hasUnsavedInk() || await confirmDiscard?.()) deactivateDrawMode();
  });
  $('iov-save-btn').addEventListener('click', () => { void finishDrawMode(); });

  window.addEventListener('resize', () => { if (ov.active) positionOverlay(); });
  new ResizeObserver(() => { if (ov.active) positionOverlay(); }).observe(quill.root);
  window.visualViewport?.addEventListener('resize', () => { if (ov.active) positionOverlay(); });
}
